import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';
import logger from '../utils/logger';

const ConsultantResultsView = () => {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [session, setSession] = useState(null);
  const [assessment, setAssessment] = useState(null);
  const [recommendations, setRecommendations] = useState([]);
  const [benchmarks, setBenchmarks] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [exporting, setExporting] = useState(false);
  const [exportFormat, setExportFormat] = useState('pdf');

  const backendUrl = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    // Check consultant authentication
    if (!user) {
      navigate('/consultant/login');
      return;
    }

    if (user.role !== 'consultant') {
      navigate('/consultant/dashboard');
      return;
    }

    loadSessionResults();
  }, [sessionId, user, navigate]);

  const loadSessionResults = async () => {
    try {
      setLoading(true);
      setError('');
      
      // Fetch session details
      const sessionResponse = await fetch(`${backendUrl}/api/live-session/${sessionId}`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include'
      });

      if (!sessionResponse.ok) {
        throw new Error('Failed to fetch session details');
      }

      const sessionData = await sessionResponse.json();
      setSession(sessionData.session || sessionData);

      // Check if session has an associated assessment
      const sessionInfo = sessionData.session || sessionData;
      const assessmentId = sessionInfo.assessment_id;
      
      // Get industry name from session.industry object or fallback
      const industryName = sessionInfo.industry?.name || sessionInfo.industry_name || 'Unknown Industry';
      const userName = sessionInfo.user?.name || 'Unknown User';
      const userEmail = sessionInfo.user?.email || '';

      if (assessmentId) {
        // Fetch assessment results
        const assessmentResponse = await fetch(`${backendUrl}/api/assessment/results/${assessmentId}`, {
          method: 'GET',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include'
        });

        if (assessmentResponse.ok) {
          const resultsData = await assessmentResponse.json();
          
          setAssessment({
            id: assessmentId,
            facility_name: resultsData.facility_name || sessionInfo.company_name || 'Unknown Facility',
            industry_name: resultsData.industry_name || industryName,
            contact_name: sessionInfo.contact_name || userName,
            contact_email: sessionInfo.contact_email || userEmail,
            annual_energy_spend: sessionInfo.annual_energy_spend || 0,
            completion_date: sessionInfo.ended_at || sessionInfo.completed_at,
            consultant_name: user.name,
            session_duration: 0 // Calculate if needed
          });

          setRecommendations(resultsData.recommendations || []);
          setBenchmarks(resultsData.benchmarks || null);
        }
      } else {
        // Session completed but no assessment yet - show basic info
        setAssessment({
          id: sessionId,
          facility_name: sessionInfo.company_name || 'Unknown Facility',
          industry_name: industryName,
          contact_name: sessionInfo.contact_name || userName,
          contact_email: sessionInfo.contact_email || userEmail,
          annual_energy_spend: sessionInfo.annual_energy_spend || 0,
          completion_date: sessionInfo.ended_at || sessionInfo.completed_at,
          consultant_name: user.name,
          session_duration: 0
        });
        
        setRecommendations([]);
        setBenchmarks(null);
      }
      
    } catch (error) {
      logger.error('Error loading session results:', error);
      setError(error.message || 'Failed to load session results');
      toast.error('Failed to load session results');
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async (format) => {
    setExporting(true);
    setExportFormat(format);

    try {
      // In production, this would call backend API to generate exports
      setTimeout(() => {
        const exportData = generateExportData(format);
        downloadFile(exportData, format);
        setExporting(false);
      }, 2000);

    } catch (error) {
      logger.error('Export error:', error);
      setExporting(false);
    }
  };

  const generateExportData = (format) => {
    const totalSavings = recommendations.reduce((sum, rec) => sum + (rec.annual_savings || 0), 0);
    const avgPayback = recommendations.reduce((sum, rec) => sum + (rec.payback_years || 0), 0) / recommendations.length;

    if (format === 'pdf') {
      return {
        filename: `${assessment.facility_name.replace(/\s+/g, '_')}_Energy_Assessment_Report.pdf`,
        content: 'PDF content would be generated here',
        mimeType: 'application/pdf'
      };
    } else if (format === 'excel') {
      return {
        filename: `${assessment.facility_name.replace(/\s+/g, '_')}_Implementation_Plan.xlsx`,
        content: 'Excel content would be generated here',
        mimeType: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      };
    } else if (format === 'powerpoint') {
      return {
        filename: `${assessment.facility_name.replace(/\s+/g, '_')}_Executive_Summary.pptx`,
        content: 'PowerPoint content would be generated here',
        mimeType: 'application/vnd.openxmlformats-officedocument.presentationml.presentation'
      };
    }
  };

  const downloadFile = (exportData, format) => {
    // Create blob and download link
    const blob = new Blob([exportData.content], { type: exportData.mimeType });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = exportData.filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  const sendFollowUpEmail = () => {
    const totalSavings = recommendations.reduce((sum, rec) => sum + (rec.annual_savings || 0), 0);
    const subject = encodeURIComponent(`Energy Assessment Results - ${assessment.facility_name}`);
    const body = encodeURIComponent(
      `Dear ${assessment.contact_name},\n\n` +
      `Thank you for participating in our energy assessment session today. \n\n` +
      `Your assessment identified potential annual savings of $${totalSavings.toLocaleString()}/year ` +
      `across ${recommendations.length} key recommendations.\n\n` +
      `I'll be following up with your detailed implementation plan and next steps.\n\n` +
      `Best regards,\n${user.name}`
    );
    
    window.location.href = `mailto:${assessment.contact_email}?subject=${subject}&body=${body}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-500 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading session results...</p>
        </div>
      </div>
    );
  }

  if (error || !assessment) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center max-w-md">
          <div className="bg-red-50 border border-red-200 rounded-lg p-6 mb-4">
            <svg className="w-12 h-12 text-red-500 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <h3 className="text-lg font-semibold text-red-900 mb-2">Failed to Load Results</h3>
            <p className="text-red-700 text-sm">{error || 'Session not found or no results available'}</p>
          </div>
          <button
            onClick={() => navigate('/consultant/dashboard')}
            className="bg-emerald-600 hover:bg-emerald-700 text-white px-6 py-2 rounded-lg font-medium transition-colors"
          >
            Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  const totalSavings = recommendations.reduce((sum, rec) => sum + (rec.annual_savings || 0), 0);
  const avgPayback = recommendations.length > 0 
    ? recommendations.reduce((sum, rec) => sum + (rec.payback_years || 0), 0) / recommendations.length 
    : 0;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate('/consultant/dashboard')}
                className="text-gray-500 hover:text-gray-700"
              >
                ← Back to Dashboard
              </button>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Session Results</h1>
                <p className="text-gray-600">{assessment.facility_name}</p>
              </div>
            </div>
            
            <div className="flex items-center space-x-3">
              <button
                onClick={sendFollowUpEmail}
                className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-medium transition-colors"
              >
                Send Follow-up Email
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Export Tools */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-8">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">Export & Share</h2>
            <p className="text-gray-600 text-sm mt-1">Generate professional reports for client follow-up</p>
          </div>
          
          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <button
                onClick={() => handleExport('pdf')}
                disabled={exporting && exportFormat === 'pdf'}
                className="flex items-center justify-center p-4 border-2 border-emerald-200 rounded-lg hover:border-emerald-300 hover:bg-emerald-50 transition-colors disabled:opacity-50"
              >
                <div className="text-center">
                  <svg className="w-8 h-8 text-emerald-600 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  <div className="font-medium text-gray-900">Professional PDF</div>
                  <div className="text-xs text-gray-600">Complete assessment report</div>
                  {exporting && exportFormat === 'pdf' && (
                    <div className="mt-2">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-emerald-600 mx-auto"></div>
                    </div>
                  )}
                </div>
              </button>

              <button
                onClick={() => handleExport('excel')}
                disabled={exporting && exportFormat === 'excel'}
                className="flex items-center justify-center p-4 border-2 border-green-200 rounded-lg hover:border-green-300 hover:bg-green-50 transition-colors disabled:opacity-50"
              >
                <div className="text-center">
                  <svg className="w-8 h-8 text-green-600 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17V7m0 10a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2h2a2 2 0 012 2m0 10a2 2 0 002 2h2a2 2 0 002-2M9 7a2 2 0 012-2h2a2 2 0 012 2m0 10V7m0 10a2 2 0 002 2h2a2 2 0 002-2V7a2 2 0 00-2-2H9z" />
                  </svg>
                  <div className="font-medium text-gray-900">Implementation Plan</div>
                  <div className="text-xs text-gray-600">Excel tracking sheet</div>
                  {exporting && exportFormat === 'excel' && (
                    <div className="mt-2">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-green-600 mx-auto"></div>
                    </div>
                  )}
                </div>
              </button>

              <button
                onClick={() => handleExport('powerpoint')}
                disabled={exporting && exportFormat === 'powerpoint'}
                className="flex items-center justify-center p-4 border-2 border-orange-200 rounded-lg hover:border-orange-300 hover:bg-orange-50 transition-colors disabled:opacity-50"
              >
                <div className="text-center">
                  <svg className="w-8 h-8 text-orange-600 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 4V2a1 1 0 011-1h8a1 1 0 011 1v2m0 0V3a1 1 0 011 1v10a1 1 0 01-1 1H8a1 1 0 01-1-1V4m0 0H6a1 1 0 00-1 1v11a1 1 0 001 1h12a1 1 0 001-1V5a1 1 0 00-1-1h-1" />
                  </svg>
                  <div className="font-medium text-gray-900">Executive Summary</div>
                  <div className="text-xs text-gray-600">PowerPoint presentation</div>
                  {exporting && exportFormat === 'powerpoint' && (
                    <div className="mt-2">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-orange-600 mx-auto"></div>
                    </div>
                  )}
                </div>
              </button>
            </div>
          </div>
        </div>

        {/* Summary Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-200">
            <div className="text-center">
              <div className="text-3xl font-bold text-emerald-600 mb-2">
                ${totalSavings.toLocaleString()}
              </div>
              <div className="text-gray-600 text-sm">Total Annual Savings</div>
            </div>
          </div>
          
          <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-200">
            <div className="text-center">
              <div className="text-3xl font-bold text-blue-600 mb-2">
                {avgPayback.toFixed(1)} years
              </div>
              <div className="text-gray-600 text-sm">Average Payback</div>
            </div>
          </div>
          
          <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-200">
            <div className="text-center">
              <div className="text-3xl font-bold text-purple-600 mb-2">
                {recommendations.length}
              </div>
              <div className="text-gray-600 text-sm">Recommendations</div>
            </div>
          </div>
          
          <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-200">
            <div className="text-center">
              <div className="text-3xl font-bold text-gray-700 mb-2">
                {benchmarks?.facility_percentile || 72}th
              </div>
              <div className="text-gray-600 text-sm">Industry Percentile</div>
            </div>
          </div>
        </div>

        {/* Recommendations */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">Energy Efficiency Recommendations</h2>
          </div>
          
          <div className="divide-y divide-gray-200">
            {recommendations.length === 0 ? (
              <div className="p-8 text-center text-gray-500">
                <svg className="w-12 h-12 text-gray-300 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                <p className="text-lg font-medium text-gray-700 mb-2">No Recommendations Available</p>
                <p className="text-sm">Assessment results will be generated after the session is completed and processed.</p>
              </div>
            ) : (
              recommendations.map((rec, index) => (
              <div key={rec.id} className="p-6">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-3 mb-2">
                      <span className="flex-shrink-0 w-6 h-6 bg-emerald-500 text-white rounded-full flex items-center justify-center text-sm font-medium">
                        {index + 1}
                      </span>
                      <h3 className="text-lg font-medium text-gray-900">{rec.title}</h3>
                      <span className="px-2 py-1 text-xs font-medium bg-gray-100 text-gray-800 rounded">
                        ARC {rec.arc_code}
                      </span>
                    </div>
                    
                    <p className="text-gray-600 mb-4">{rec.description}</p>
                    
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div className="bg-emerald-50 p-3 rounded-lg">
                        <div className="text-lg font-bold text-emerald-600">
                          ${rec.annual_savings.toLocaleString()}
                        </div>
                        <div className="text-xs text-gray-600">Annual Savings</div>
                      </div>
                      
                      <div className="bg-blue-50 p-3 rounded-lg">
                        <div className="text-lg font-bold text-blue-600">
                          {rec.payback_years.toFixed(1)} years
                        </div>
                        <div className="text-xs text-gray-600">Payback Period</div>
                      </div>
                      
                      <div className="bg-gray-50 p-3 rounded-lg">
                        <div className="text-lg font-bold text-gray-700">
                          ${rec.implementation_cost.toLocaleString()}
                        </div>
                        <div className="text-xs text-gray-600">Implementation Cost</div>
                      </div>
                    </div>
                    
                    <div className="mt-4 p-3 bg-gray-50 rounded-lg">
                      <div className="text-xs font-medium text-gray-700 mb-1">Implementation Steps:</div>
                      <div className="text-sm text-gray-600">{rec.implementation_preview}</div>
                    </div>
                  </div>
                </div>
              </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConsultantResultsView;